'use client'

/**
 * batch-71 kiosk-magazijn (FO-532 §C9) - handscanner op de tablet (keyboard-wedge). Een
 * handscanner is voor de browser een toetsenbord: hij typt de code heel snel en sluit af
 * met Enter. Twee dingen die samen dekken wat er op de vloer gebeurt:
 *
 *  - `ScanInvoer`: een altijd zichtbaar invoerveld dat zijn focus terugpakt, tenzij je in
 *    een ander veld bezig bent (aantal, reden). Ook met de hand te typen.
 *  - `useHandscanner`: vangt een scan op als de focus nergens staat (bv. net op een knop
 *    getikt). Typt iemand in een ander veld, dan blijft dat veld van hem.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { extractQrToken } from '../../lib/qr-token'

/** Sneller dan dit tussen twee tekens is een scanner, geen mens. */
const MAX_MS_TUSSEN_TEKENS = 80
const MIN_LENGTE = 4

function isBewerkbaar(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  if (el.isContentEditable) return true
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT'
}

/** Geeft elke scan door als ruwe tekst, zolang de focus niet in een invoerveld staat. */
export function useHandscanner(onScan: (tekst: string) => void, actief = true) {
  const onScanRef = useRef(onScan)
  onScanRef.current = onScan

  useEffect(() => {
    if (!actief) return
    let buffer = ''
    let laatste = 0
    function opToets(e: KeyboardEvent) {
      if (isBewerkbaar(e.target) || e.ctrlKey || e.metaKey || e.altKey) return
      const nu = performance.now()
      if (nu - laatste > MAX_MS_TUSSEN_TEKENS) buffer = ''
      laatste = nu
      if (e.key === 'Enter') {
        if (buffer.length >= MIN_LENGTE) {
          e.preventDefault()
          e.stopPropagation()
          onScanRef.current(buffer)
        }
        buffer = ''
        return
      }
      if (e.key.length === 1) buffer += e.key
    }
    window.addEventListener('keydown', opToets, true)
    return () => window.removeEventListener('keydown', opToets, true)
  }, [actief])
}

export function ScanInvoer({
  onScan,
  bezig = false,
  label = 'Scan een code',
  placeholder = 'Scan met de handscanner of typ de code',
  vangFocus = true,
  className = '',
  grootte = 'groot',
  ruw = false,
}: {
  /** Het token uit de scan (of de ruwe tekst als `ruw` aan staat). */
  onScan: (token: string) => void
  bezig?: boolean
  label?: string
  placeholder?: string
  /** Pak de focus terug als hij nergens anders op een invoerveld staat. */
  vangFocus?: boolean
  className?: string
  grootte?: 'groot' | 'normaal'
  ruw?: boolean
}) {
  const invoerRef = useRef<HTMLInputElement>(null)
  const [waarde, setWaarde] = useState('')
  const [fout, setFout] = useState<string | null>(null)
  const onScanRef = useRef(onScan)
  onScanRef.current = onScan

  const verwerk = useCallback(
    (tekst: string) => {
      const schoon = tekst.trim()
      if (!schoon) return
      const token = ruw ? schoon : extractQrToken(schoon)
      if (!token) {
        setFout('Deze code is niet herkend. Scan opnieuw of typ de code onder de QR.')
        return
      }
      setFout(null)
      onScanRef.current(token)
    },
    [ruw],
  )

  // De focus terugpakken zodra hij weg is, tenzij iemand in een ander veld werkt.
  useEffect(() => {
    if (!vangFocus) return
    const veld = invoerRef.current
    veld?.focus()
    const timer = window.setInterval(() => {
      const actief = document.activeElement
      if (actief === invoerRef.current) return
      if (isBewerkbaar(actief)) return
      if (document.querySelector('[role="dialog"]')) return
      invoerRef.current?.focus({ preventScroll: true })
    }, 800)
    return () => window.clearInterval(timer)
  }, [vangFocus])

  // Staat de focus even nergens? Dan vangt de hook de scan alsnog op.
  useHandscanner(verwerk, vangFocus)

  const hoogte = grootte === 'groot' ? 'min-h-[64px] text-xl' : 'min-h-[56px] text-lg'

  return (
    <form
      className={className}
      onSubmit={(e) => {
        e.preventDefault()
        const tekst = waarde
        setWaarde('')
        verwerk(tekst)
      }}
    >
      <label className="block">
        <span className="mb-1 block text-sm font-semibold text-streep-donker">{label}</span>
        <input
          ref={invoerRef}
          value={waarde}
          onChange={(e) => {
            setWaarde(e.target.value)
            if (fout) setFout(null)
          }}
          data-scan-invoer=""
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="go"
          disabled={bezig}
          placeholder={placeholder}
          aria-invalid={fout ? true : undefined}
          className={`w-full rounded-xl border-2 border-streep-donker bg-streep-surface px-4 text-streep-donker placeholder:text-streep-text-muted focus:border-streep-groen focus:outline-none focus:ring-2 focus:ring-streep-groen disabled:opacity-60 ${hoogte}`}
        />
      </label>
      {fout ? (
        <p role="alert" className="mt-1 text-base font-medium text-red-700">
          {fout}
        </p>
      ) : null}
    </form>
  )
}
